// OnyxATSTemplate.tsx
import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  Link,
  StyleSheet,
  Image,
} from '@react-pdf/renderer';
import _ from 'lodash';
import { util } from '@helper';

/**
 * OnyxATSTemplate
 * - Onyx-inspired header and crimson accent line
 * - ATS-friendly: all selectable text, no icons/tables/images
 * - Flat "Skills" list (comma-separated or stacked)
 * - Right-aligned date/location metadata on Experience/Education
 */

const ACCENT = '#b91c1c'; // deep red (Onyx vibe)
const TEXT = '#111';
const MUTED = '#444';

const styles = StyleSheet.create({
  page: {
    paddingTop: 34,
    paddingBottom: 42,
    paddingHorizontal: 44,
    color: TEXT,
  },

  // Header
  name: { fontSize: 22, fontWeight: 'bold', letterSpacing: 0.2 },
  tagline: { fontSize: 11, marginTop: 2, color: MUTED },

  // Contacts line (ATS-safe plain text; links embedded
  // but visible text remains)
  contacts: { fontSize: 9, marginTop: 6, lineHeight: 1.4 },

  // Accent rule
  accentRule: {
    height: 2,
    backgroundColor: ACCENT,
    marginVertical: 12,
    opacity: 0.9,
  },

  // Section header
  section: { marginTop: 12 },
  sectionHeader: {
    fontSize: 12,
    fontWeight: 'bold',
    color: ACCENT,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: 6,
  },

  // Entry rows
  entry: { marginTop: 0 },
  entryRow: { flexDirection: 'row', justifyContent: 'space-between' },
  entryLeft: { flex: 1, paddingRight: 10 },
  entryRight: { width: 170, alignItems: 'flex-end' },

  entryTitle: { fontSize: 9.85, fontWeight: 'bold', marginBottom: 3 },
  entrySub: { fontSize: 9.2 }, // role/degree
  meta: { fontSize: 8.5, color: MUTED, marginTop: 1, textAlign: 'right' },

  // Paragraph text
  p: { fontSize: 9, lineHeight: 1.37 },
  bulletProject: {
    fontSize: 8.5,
    marginTop: 8,
    color: '#333',
    lineHeight: 1.37,
    width: 450,
  },
  bulletProject2: {
    fontSize: 8.5,
    marginTop: 2,
    marginBottom: 8,
    color: '#333',
    lineHeight: 1.37,
    width: 450,
  },

  // Link line (for websites under each item)
  linkLine: { fontSize: 9, color: TEXT, marginTop: 2 },

  // Bullets (clean hanging indent)
  bullets: { marginTop: 6 },
  bulletRow: { flexDirection: 'row', marginBottom: 3 },
  bulletMark: {
    width: 10,
    textAlign: 'center',
    color: ACCENT,
    fontSize: 11,
    marginTop: -0.3,
  },
  bulletText: { flex: 1, fontSize: 8.75, lineHeight: 1.25 },

  // Skills
  skillsWrap: { marginTop: 6 },
  skillLine: { fontSize: 9.2, lineHeight: 1.38 },
  skillItem: { fontSize: 9.2, lineHeight: 1.38, marginBottom: 2 },

  // Small divider to separate bottom clusters (optional)
  faintRule: {
    height: 1,
    backgroundColor: '#ddd',
    marginTop: 12,
    opacity: 0.8,
  },

  // Image mosaic
  mosaicImage: { width: '100%', height: '10vh' },
});

// ---------- Types ----------
type Contact = { label?: string; text?: string; href?: string };
type Experience = {
  company: string;
  role: string;
  start: string;
  end: string; // "Present" allowed
  location?: string;
  jobType?: string;
  url?: string; // company or project site
  bullets?: string[];
};
type Education = {
  school: string;
  degree?: string;
  major?: string;
  start?: string;
  end?: string;
  location?: string;
  achievement?: string;
};
type Project = {
  name: string;
  role?: string;
  year?: string | number;
  blurb?: string;
  url?: string;
};
type Volunteer = {
  name: string;
  role?: string;
  duration?: string;
  blurb?: string;
};
type Certification = { title: string; org?: string; year?: string | number };

export type OnyxATSData = {
  name: string;
  tagline?: string;
  contacts?: Contact[]; // rendered in one readable line
  summary?: string | null;
  experience?: Experience[];
  education?: Education[];
  projects?: Project[];
  volunteers?: Volunteer[];
  certifications?: Certification[];
  skills?: string[]; // FLAT list (not grouped)
  languages?: string[];
  references?: string;
};

const renderAllBullet = (items: any[]): any =>
  items.map((t: string, i: number) => (
    <View key={i} style={styles.bulletRow}>
      <Text style={styles.bulletMark}>•</Text>
      <Text style={styles.bulletText}>{t}</Text>
    </View>
  ));

const renderBullet = (items: any[], n: number): any =>
  items.slice(0, n).map((t: string, i: number) => (
    <View key={i} style={styles.bulletRow}>
      <Text style={styles.bulletMark}>•</Text>
      <Text style={styles.bulletText}>{t}</Text>
    </View>
  ));

export const OnyxATSTemplate: React.FC<{
  data: OnyxATSData;
  isPreview: boolean;
}> = ({ data, isPreview = true }) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <Text style={styles.name}>{data.name}</Text>
        {data.tagline ? (
          <Text style={styles.tagline}>{data.tagline}</Text>
        ) : null}

        {/* Contacts (single parsable line with inline links) */}
        {data.contacts?.length ? (
          <Text style={styles.contacts}>
            {data.contacts.map((c, i) => {
              const label = c.label ?? c.text ?? c.href ?? '';
              const sep = i < data.contacts!.length - 1 ? '  •  ' : '';
              return c.href ? (
                <Text key={i}>
                  <Link src={c.href}>{label}</Link>
                  {sep}
                </Text>
              ) : (
                <Text key={i}>
                  {label}
                  {sep}
                </Text>
              );
            })}
          </Text>
        ) : null}

        <View style={styles.accentRule} />

        {/* Summary */}
        {data.summary ? (
          <View style={styles.section}>
            <Text style={styles.sectionHeader}>Summary</Text>
            <Text style={styles.p}>
              {isPreview ? util.limitWords(data.summary) : data.summary}
            </Text>
          </View>
        ) : null}

        {/* Education */}
        <View style={styles.section}>
          <Text style={styles.sectionHeader}>Education</Text>
          {_.map(data.education || [], (_data) => (
            <View style={styles.entry}>
              <View style={styles.entryRow}>
                <View style={styles.entryLeft}>
                  <Text style={styles.entryTitle}>{_data.school}</Text>
                  {_data.degree ? (
                    <Text style={styles.entrySub}>
                      {_data.major || '[Major]'}
                      {' • '}
                      {_data.degree || '[Degree]'}
                    </Text>
                  ) : null}
                  {_data.achievement ? (
                    <Text style={styles.bulletProject}>
                      {isPreview
                        ? util.limitWords(_data.achievement)
                        : _data.achievement}
                    </Text>
                  ) : null}
                  {isPreview && util.countWords(_data.achievement) > 20 && (
                    <Image
                      style={{ width: '155%', height: '10vh' }}
                      src="/img-censor-mosaic.jpg"
                    />
                  )}
                </View>
                <View style={styles.entryRight}>
                  <Text style={styles.meta}>
                    {[_data.start, _data.end].filter(Boolean).join(' to ')}
                  </Text>
                  {_data.location ? (
                    <Text style={styles.meta}>{_data.location}</Text>
                  ) : null}
                </View>
              </View>
            </View>
          ))}
        </View>

        {/* Experience */}
        {data.experience?.length ? (
          <View style={styles.section}>
            <Text style={styles.sectionHeader}>Experience</Text>
            {data.experience.map((ex, i) => (
              <View key={i} style={styles.entry}>
                <View style={styles.entryRow}>
                  <View style={styles.entryLeft}>
                    <Text style={styles.entryTitle}>{ex.company}</Text>
                    <Text style={styles.p}>
                      {ex.role}
                      {' • '}
                      {ex.jobType}
                    </Text>
                  </View>
                  <View style={styles.entryRight}>
                    <Text style={styles.meta}>
                      {ex.start} to {ex.end}
                    </Text>
                    {ex.location ? (
                      <Text style={styles.meta}>{ex.location}</Text>
                    ) : null}
                  </View>
                </View>
                <View style={styles.bullets}>
                  {isPreview
                    ? renderBullet(ex.bullets || [], 1)
                    : renderAllBullet(ex.bullets || [])}
                  {isPreview && (ex.bullets || []).length >= 2 && (
                    <Image
                      style={styles.mosaicImage}
                      src="/img-censor-mosaic.jpg"
                    />
                  )}
                </View>
              </View>
            ))}
          </View>
        ) : null}

        {/* Projects */}
        {data.projects?.length ? (
          <View style={styles.section}>
            <Text style={styles.sectionHeader}>Projects</Text>
            {data.projects.map((p, i) => (
              <View key={i} style={styles.entry}>
                <Text style={styles.entryTitle}>{p.name}</Text>
                <View key={i} style={styles.entryRow}>
                  {p.role ? (
                    <Text style={styles.entrySub}>{p.role}</Text>
                  ) : null}
                  {p.year ? <Text style={styles.p}>{p.year}</Text> : null}
                </View>
                {p.url ? (
                  <Text style={styles.linkLine}>
                    <Link src={p.url}>{p.url}</Link>
                  </Text>
                ) : null}
                {p.blurb && (
                  <Text style={styles.bulletProject2}>{p.blurb}</Text>
                )}
              </View>
            ))}
          </View>
        ) : null}

        {/* Volunteers */}
        {data.volunteers?.length ? (
          <View style={styles.section}>
            <Text style={styles.sectionHeader}>Volunteers</Text>
            {data.volunteers.map((p, i) => (
              <View key={i} style={styles.entry}>
                <Text style={styles.entryTitle}>{p.name}</Text>
                <View key={i} style={styles.entryRow}>
                  {p.role ? (
                    <Text style={styles.entrySub}>{p.role}</Text>
                  ) : null}
                  {p.duration ? (
                    <Text style={styles.p}>{p.duration}</Text>
                  ) : null}
                </View>
                {p.blurb ? (
                  <Text style={styles.bulletProject2}>
                    {isPreview ? null : p.blurb}
                  </Text>
                ) : null}
                {p.blurb && isPreview && util.countWords(p.blurb) > 5 && (
                  <Image
                    style={styles.mosaicImage}
                    src="/img-censor-mosaic.jpg"
                  />
                )}
              </View>
            ))}
          </View>
        ) : null}

        {/* Certifications */}
        {data.certifications?.length ? (
          <View style={styles.section}>
            <Text style={styles.sectionHeader}>Certifications</Text>
            {!isPreview &&
              data.certifications.map((c, i) => (
                <View key={i} style={styles.entryRow}>
                  <Text style={styles.p}>
                    {c.title}
                    {c.org ? ` issued by ${c.org}` : ''}
                  </Text>
                  <Text style={styles.p}>{c.year ?? ''}</Text>
                </View>
              ))}
            {isPreview &&
              data.certifications.splice(0, 2).map((c, i) => (
                <View key={i} style={styles.entryRow}>
                  <Text style={styles.p}>
                    {c.title}
                    {c.org ? ` issued by ${c.org}` : ''}
                  </Text>
                  <Text style={styles.p}>{c.year ?? ''}</Text>
                </View>
              ))}
            {isPreview && data.certifications.length > 1 ? (
              <Image style={styles.mosaicImage} src="/img-censor-mosaic.jpg" />
            ) : null}
          </View>
        ) : null}

        {/* Skills — flat list */}
        {data.skills?.length ? (
          <View style={styles.section}>
            <Text style={styles.sectionHeader}>Skills</Text>
            <Text style={styles.skillLine}>{data.skills.join(', ')}</Text>
          </View>
        ) : null}

        {/* Languages — flat list */}
        {data.languages?.length ? (
          <View style={styles.section}>
            <Text style={styles.sectionHeader}>Languages</Text>
            <Text style={styles.skillLine}>{data.languages.join(', ')}</Text>
          </View>
        ) : null}

        {/* References */}
        {data.references ? (
          <View style={styles.section}>
            <Text style={styles.sectionHeader}>References</Text>
            <Text style={styles.p}>{data.references}</Text>
          </View>
        ) : null}

        <View style={styles.faintRule} />
      </Page>
    </Document>
  );
};
